/**
 * The wild treatment — a holographic foil pass for **J♠ (`js`) and J♣ (`jc`) only**.
 *
 * J♥ and J♦ are ordinary tens and never touch this file. Mistaking one for the
 * other is a gameplay error, not a cosmetic one, so membership is decided by
 * `WILD_CARDS` from the rules engine and nothing else.
 *
 * It runs as a `Filter` rather than as part of the card shader on purpose: the
 * violet rim and the reveal flare have to bleed *outside* the card's silhouette,
 * and only a filter gets padding. Everything in it is driven in card-local
 * design units, so the treatment stays glued to the card at any resolution:
 *
 * - **Iridescence** whose hue is a function of where the card sits on the table
 *   (the stand-in for view angle), where the fragment sits on the card, the
 *   luminance of the art printed underneath, and time.
 * - A **travelling specular sweep** that crosses the face diagonally and then
 *   parks off-card until the next pass.
 * - A fine **lenticular micro-ruling**, antialiased against its own screen
 *   footprint so it shimmers instead of moiréing.
 * - A **violet rim** derived from the card's own alpha edge, plus an outer glow.
 * - A **reveal flare**: a hard spike that decays into a slow breath.
 *
 * `prefers-reduced-motion` freezes the sweep and the hue drift and drops the
 * flare entirely.
 */

import { Filter, GlProgram, UniformGroup } from 'pixi.js';

import { palette } from '@/core/tokens';
import { WILD_CARDS, type CardId } from '@/game/types';
import { CARD } from '@/pixi/layout';

/* ------------------------------------------------------------------ *
 * Tuning
 * ------------------------------------------------------------------ */

export const FOIL = {
  /** Filter padding, in screen pixels — the room the rim glow needs. */
  padding: 30,
  /** Period of the lenticular ruling, in design units. */
  rulingPeriod: 4.2,
  /** Seconds between sweeps. */
  sweepPeriod: 2.9,
  /**
   * Seconds one sweep takes to cross the card.
   *
   * Quicker than it was (0.95). A specular highlight is a reflection of a
   * moving light, and a reflection that takes most of a second to cross a
   * playing card reads as a glow drifting rather than as light catching an
   * edge. Shortening it is what makes the sweep directional — you see it go
   * somewhere, instead of seeing the card be bright for a while.
   */
  sweepTravel: 0.62,
  /** Reveal flare: attack, exponential decay rate, and total life in seconds. */
  flareAttack: 0.075,
  flareDecay: 4.6,
  flareLife: 1.9,
  /** Slow breath the foil settles into, in radians per second. */
  breathRate: 1.35,
  /** Inner rim sampling radius, in design units. */
  rimInner: 3.2,
  /**
   * Outer glow sampling radius, in design units.
   *
   * Was 15, which at rest put a soft violet halo a centimetre out from the
   * card on a phone — the thing that reads as a sticker with a light behind it
   * rather than as foil. At 9 the falloff is short enough that the eye reads a
   * lit *edge*, and the energy that used to be spent on the halo is spent on
   * the arrival flare instead, where it is a moment rather than a permanent
   * state. The rim is still the widest thing here, so `padding` is unchanged.
   */
  rimOuter: 9,
} as const;

let reducedMotion: boolean | null = null;

/** True when the user has asked the OS for less animation. */
export function prefersReducedMotion(): boolean {
  if (reducedMotion === null) {
    reducedMotion =
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return reducedMotion;
}

/** Test seam: force the reduced-motion branch on or off. */
export function setReducedMotionOverride(value: boolean | null): void {
  reducedMotion = value;
}

/** True for J♠ and J♣ only. J♥ and J♦ are ordinary tens. */
export function isWildCard(id: CardId): boolean {
  return WILD_CARDS.includes(id);
}

/* ------------------------------------------------------------------ *
 * GLSL
 * ------------------------------------------------------------------ */

function f(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : `${value}`;
}

function glslRgb(hex: number): string {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8) & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  return `vec3(${r.toFixed(5)}, ${g.toFixed(5)}, ${b.toFixed(5)})`;
}

function mixHex(a: number, b: number, t: number): number {
  const ch = (v: number, shift: number) => (v >> shift) & 0xff;
  const r = Math.round(ch(a, 16) + (ch(b, 16) - ch(a, 16)) * t);
  const g = Math.round(ch(a, 8) + (ch(b, 8) - ch(a, 8)) * t);
  const bl = Math.round(ch(a, 0) + (ch(b, 0) - ch(a, 0)) * t);
  return (r << 16) | (g << 8) | bl;
}

const VERTEX = /* glsl */ `#version 300 es
precision highp float;

in vec2 aPosition;

out vec2 vTextureCoord;
out vec2 vFrame;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

void main(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    gl_Position = vec4(position, 0.0, 1.0);

    vTextureCoord = aPosition * (uOutputFrame.zw * uInputSize.zw);
    // 0..1 across the padded region the filter was given — the only stable
    // handle a filter has on "where am I on the object".
    vFrame = aPosition;
}
`;

const FRAGMENT = /* glsl */ `#version 300 es
precision highp float;

in vec2 vTextureCoord;
in vec2 vFrame;

out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform vec4 uInputClamp;
uniform vec4 uOutputFrame;

uniform vec2 uCardPos;
uniform vec2 uCardRot;
uniform float uPixelScale;
uniform float uTime;
uniform float uSweep;
uniform float uIntensity;
uniform float uFlare;
uniform float uAlpha;

const vec3 C0 = ${glslRgb(palette.violet)};
const vec3 C1 = ${glslRgb(palette.violetLight)};
const vec3 C2 = ${glslRgb(palette.gold)};
const vec3 C3 = ${glslRgb(palette.mint)};
const vec3 RIM = ${glslRgb(palette.violetLight)};
const vec3 BURST = ${glslRgb(mixHex(palette.white, palette.violetLight, 0.4))};

const float RULING_FREQ = ${(((Math.PI * 2) / FOIL.rulingPeriod)).toFixed(6)};
const float SWEEP_SPAN  = ${f(Math.hypot(CARD.width, CARD.height))};
const vec2  CARD_HALF   = vec2(${f(CARD.width / 2)}, ${f(CARD.height / 2)});
const float RIM_INNER   = ${f(FOIL.rimInner)};
const float RIM_OUTER   = ${f(FOIL.rimOuter)};

/** Four house colours looped into a foil rainbow. */
vec3 iridescence(float t) {
    float p = fract(t) * 4.0;
    float i = floor(p);
    float k = p - i;
    k = k * k * (3.0 - 2.0 * k);
    vec3 a = i < 0.5 ? C0 : (i < 1.5 ? C1 : (i < 2.5 ? C2 : C3));
    vec3 b = i < 0.5 ? C1 : (i < 1.5 ? C2 : (i < 2.5 ? C3 : C0));
    return mix(a, b, k);
}

float sampleAlpha(vec2 uv) {
    return texture(uTexture, clamp(uv, uInputClamp.xy, uInputClamp.zw)).a;
}

void main(void) {
    vec4 src = texture(uTexture, vTextureCoord);   // premultiplied
    float a = src.a;

    // Card-local coordinates in design units. uOutputFrame.zw is the region in
    // screen pixels and uPixelScale is design-to-screen, so this is exact at any
    // letterbox scale; the rotation then puts it in the card's own frame.
    vec2 regionDesign = uOutputFrame.zw / max(uPixelScale, 0.0001);
    vec2 c = (vFrame - 0.5) * regionDesign;
    vec2 cl = vec2(
        c.x * uCardRot.x + c.y * uCardRot.y,
       -c.x * uCardRot.y + c.y * uCardRot.x);

    // Luminance of the art printed under the foil, un-premultiplied.
    float lum = dot(src.rgb, vec3(0.299, 0.587, 0.114)) / max(a, 0.0015);

    // Lenticular micro-ruling, faded out by its own screen footprint so it
    // never aliases into moiré when the card is small or tilted away.
    float rulPhase = cl.y * RULING_FREQ;
    float rulWidth = fwidth(rulPhase);
    float ruling = sin(rulPhase) / (1.0 + rulWidth * rulWidth * 0.6);

    // The hue depends on where the card is on the table — the 2D stand-in for
    // view angle — as well as where we are on the card, what is printed
    // underneath, the ruling, and time.
    float phase = dot(uCardPos, vec2(0.00042, 0.00071))
                + cl.x * 0.0092 + cl.y * 0.0061
                + lum * 0.45
                + ruling * 0.06
                + uTime * 0.085;

    vec3 irid = iridescence(phase);

    // Travelling specular sweep, diagonally across the face.
    float sweepCoord = (cl.x * 0.62 + cl.y * 0.79) / SWEEP_SPAN + 0.5;
    // Narrower than it was (5.2). A wide gaussian is a wash; a tight one is a
    // highlight with a direction, which is the whole point of a sweep.
    float sd = (sweepCoord - uSweep) * 8.4;
    float band = exp(-sd * sd);

    float gain = uIntensity * uAlpha;
    float foil = gain * a;

    // Foil is a coating, not a lamp. It tints and *darkens* the print underneath
    // as much as it brightens it — a purely additive rainbow would just blow the
    // paper out and take the pips with it. So the bulk of the effect is a
    // multiply, and only the sweep itself is allowed to add light.
    float coat = clamp(gain * (0.50 + 0.30 * band), 0.0, 0.72);
    vec3 coated = src.rgb * (0.60 + 1.05 * irid + 0.17 * ruling);
    vec3 rgb = mix(src.rgb, coated, coat);
    rgb += irid * (foil * (0.05 + 0.30 * band));
    // The band is narrower now, so it can afford to be brighter without
    // blowing the print out — the same light through a smaller aperture.
    rgb += vec3(1.0) * (foil * band * (0.16 + 0.62 * uFlare));

    // Edge detection on the card's own alpha: an inner rim and an outer glow,
    // both of which follow the silhouette however the card is turned.
    float px = uInputSize.z;
    float py = uInputSize.w;
    float r1 = RIM_INNER * uPixelScale;
    float r2 = min(RIM_OUTER * uPixelScale, ${f(FOIL.padding - 6)});
    float amin = a;
    float amax1 = a;
    float amax2 = a;
    for (int i = 0; i < 8; i++) {
        float ang = float(i) * 0.7853981634;
        vec2 dir = vec2(cos(ang), sin(ang)) * vec2(px, py);
        float s1 = sampleAlpha(vTextureCoord + dir * r1);
        float s2 = sampleAlpha(vTextureCoord + dir * r2);
        amin = min(amin, s1);
        amax1 = max(amax1, s1);
        amax2 = max(amax2, s2);
    }

    float inner = a * (1.0 - amin);
    float glow = (1.0 - a) * max(amax1, amax2 * 0.7);

    // Rest and arrival pull apart deliberately. The resting coefficients come
    // down and the flare's go up, so a wild sitting on the stock is a card with
    // a lit edge, and a wild *arriving* is an event. Previously the two were
    // close enough that the flare barely registered over the idle state.
    rgb += RIM * (inner * gain * (0.42 + 2.6 * uFlare));
    float glowA = glow * gain * (0.18 + 1.9 * uFlare);
    rgb += RIM * glowA;

    // The reveal burst: a soft blow-out from the centre of the card.
    vec2 rn = cl / CARD_HALF;
    float burst = uFlare * exp(-dot(rn, rn) * 2.2) * 0.7 * gain;
    rgb += BURST * (burst * a);

    float outA = a + glowA * (1.0 - a);
    finalColor = vec4(rgb, outA);
}
`;

/* ------------------------------------------------------------------ *
 * Filter
 * ------------------------------------------------------------------ */

function makeUniforms() {
  return new UniformGroup({
    uCardPos: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
    uCardRot: { value: new Float32Array([1, 0]), type: 'vec2<f32>' },
    uPixelScale: { value: 1, type: 'f32' },
    uTime: { value: 0, type: 'f32' },
    uSweep: { value: -10, type: 'f32' },
    uIntensity: { value: 1, type: 'f32' },
    uFlare: { value: 0, type: 'f32' },
    uAlpha: { value: 1, type: 'f32' },
  });
}

type FoilUniformGroup = ReturnType<typeof makeUniforms>;

let program: GlProgram | null = null;

function getProgram(): GlProgram {
  program ??= GlProgram.from({ vertex: VERTEX, fragment: FRAGMENT, name: 'card-foil' });
  return program;
}

export class FoilFilter extends Filter {
  readonly foilUniforms: FoilUniformGroup;

  /** Baseline strength, before the breath and the flare are folded in. */
  #base = 1;
  #time = 0;
  #sweepClock: number = FOIL.sweepPeriod;
  /** Negative means "no reveal running". */
  #flareClock = -1;

  constructor() {
    const uniforms = makeUniforms();
    super({
      glProgram: getProgram(),
      resources: { foilUniforms: uniforms },
      padding: FOIL.padding,
      resolution: 'inherit',
      antialias: 'inherit',
    });
    this.foilUniforms = uniforms;
  }

  /** Card centre in design units — this is what makes the hue view-dependent. */
  setCardPosition(x: number, y: number): void {
    const target = this.foilUniforms.uniforms.uCardPos;
    target[0] = x;
    target[1] = y;
  }

  setCardRotation(radians: number): void {
    const target = this.foilUniforms.uniforms.uCardRot;
    target[0] = Math.cos(radians);
    target[1] = Math.sin(radians);
  }

  /** Design-units-to-screen-pixels for this card, from its world transform. */
  setPixelScale(scale: number): void {
    this.foilUniforms.uniforms.uPixelScale = scale > 0 ? scale : 1;
  }

  /** Overall opacity of the card, so a fading wild fades its glow too. */
  setAlpha(alpha: number): void {
    this.foilUniforms.uniforms.uAlpha = alpha;
  }

  /** Baseline foil strength. Raise it while the wild is the active card. */
  setBaseIntensity(value: number): void {
    this.#base = value;
  }

  /** Fire the reveal: a spike that decays into the breath. */
  reveal(): void {
    if (prefersReducedMotion()) {
      this.#flareClock = -1;
      return;
    }
    this.#flareClock = 0;
    this.#sweepClock = 0;
  }

  /** Advance the animated uniforms. `dt` is in seconds. */
  advance(dt: number): void {
    const uniforms = this.foilUniforms.uniforms;

    if (prefersReducedMotion()) {
      // Frozen, but not dead: a static hue offset and a parked sweep still read
      // as foil, and the flare is dropped entirely.
      uniforms.uTime = 0;
      uniforms.uSweep = 0.42;
      uniforms.uFlare = 0;
      uniforms.uIntensity = this.#base;
      return;
    }

    this.#time += dt;
    uniforms.uTime = this.#time;

    this.#sweepClock += dt;
    if (this.#sweepClock >= FOIL.sweepPeriod) this.#sweepClock -= FOIL.sweepPeriod;
    const travel = this.#sweepClock / FOIL.sweepTravel;
    // Between passes the band is parked far off the card, where the Gaussian
    // evaluates to nothing — cheaper and cleaner than branching.
    uniforms.uSweep = travel <= 1 ? -0.25 + travel * 1.5 : -12;

    let flare = 0;
    if (this.#flareClock >= 0) {
      this.#flareClock += dt;
      const t = this.#flareClock;
      flare =
        t < FOIL.flareAttack
          ? t / FOIL.flareAttack
          : Math.exp(-(t - FOIL.flareAttack) * FOIL.flareDecay);
      if (t >= FOIL.flareLife) {
        this.#flareClock = -1;
        flare = 0;
      }
    }
    uniforms.uFlare = flare;

    // Once the spike has gone the foil settles into a slow breath.
    const breath = 0.5 + 0.5 * Math.sin(this.#time * FOIL.breathRate);
    uniforms.uIntensity = this.#base * (0.86 + 0.16 * breath);
  }

  /** Wipe every animated value — used when a card sprite is recycled. */
  reset(): void {
    this.#base = 1;
    this.#time = 0;
    this.#sweepClock = FOIL.sweepPeriod;
    this.#flareClock = -1;
    const uniforms = this.foilUniforms.uniforms;
    uniforms.uCardPos[0] = 0;
    uniforms.uCardPos[1] = 0;
    uniforms.uCardRot[0] = 1;
    uniforms.uCardRot[1] = 0;
    uniforms.uPixelScale = 1;
    uniforms.uTime = 0;
    uniforms.uSweep = -12;
    uniforms.uIntensity = 1;
    uniforms.uFlare = 0;
    uniforms.uAlpha = 1;
  }
}
